import { useState } from "react";
import { Field, TextInput, TextArea, CollapsibleSection } from "./FormField";
import { MediaImageField } from "./MediaPickerDialog";

const SEO_DEFAULTS = {
  seo_id: 0,
  actual_url: "",
  link_url: "",
  canonical_url: "",
  title_name: "",
  favicon: "",
  image: "",
  description: "",
  keywords: "",
  pretty_function_name: "",
  call_function_name: "",
  content_type: "website",
  changefreq: "monthly",
  priority_screen: "0.8",
};

const CHANGEFREQ_OPTIONS = [
  "always",
  "hourly",
  "daily",
  "weekly",
  "monthly",
  "yearly",
  "never",
];

const PRIORITY_OPTIONS = [
  "0.0",
  "0.1",
  "0.2",
  "0.3",
  "0.4",
  "0.5",
  "0.6",
  "0.7",
  "0.8",
  "0.9",
  "1.0",
];

const selectStyle = {
  background: "hsl(var(--muted))",
  border: "1px solid hsl(var(--border))",
  color: "hsl(var(--foreground))",
};

/**
 * SEOSection Component
 *
 * Props:
 *  - seoData: object            → current SEO state (controlled)
 *  - onChange: fn(field, value) → setter callback
 *  - defaults: object           → optional field defaults (merged with SEO_DEFAULTS)
 *  - hideActualUrl: bool        → true = Actual URL field hide
 *  - hideLinkUrl: bool          → true = Link URL field hide
 *  - hideCanonicalUrl: bool     → true = Canonical URL field hide
 *  - hidePrettyUrl: bool        → true = Pretty & Call Function Name fields hide
 */
export function SEOSection({
  seoData = {},
  onChange,
  defaults = {},
  hideActualUrl = true,
  hideLinkUrl = true,
  hideCanonicalUrl = true,
  hidePrettyUrl = true,
  hideCallUrl = true,
  hideContentTypeUrl = true,
}) {
  const [active, setActive] = useState(true);

  const merged = { ...SEO_DEFAULTS, ...defaults, ...seoData };

  const set = (field) => (e) => onChange(field, e.target.value);
  const setMedia = (field) => (val) => onChange(field, val);

  return (
    <CollapsibleSection
      title="SEO Settings"
      active={active}
      onToggleActive={setActive}
    >
      {!hideActualUrl && (
        <Field label="Actual URL">
          <TextInput value={merged.actual_url} onChange={set("actual_url")} />
        </Field>
      )}
      {!hideLinkUrl && (
        <Field label="Link URL">
          <TextInput value={merged.link_url} onChange={set("link_url")} />
        </Field>
      )}
      {!hideCanonicalUrl && (
        <Field label="Canonical URL">
          <TextInput
            value={merged.canonical_url}
            onChange={set("canonical_url")}
          />
        </Field>
      )}
      <Field label="Title">
        <TextInput value={merged.title_name} onChange={set("title_name")} />
      </Field>
      <Field label="Description">
        <TextArea value={merged.description} onChange={set("description")} />
      </Field>
      <Field label="Keywords">
        <TextInput value={merged.keywords} onChange={set("keywords")} />
      </Field>
      <Field label="Favicon">
        <MediaImageField
          label="Select Favicon"
          value={merged.favicon}
          onChange={setMedia("favicon")}
          accept="image"
        />
      </Field>
      <Field label="OG Image">
        <MediaImageField
          label="Select OG Image"
          value={merged.image}
          onChange={setMedia("image")}
          accept="image"
        />
      </Field>
      {!hidePrettyUrl && (
        <Field label="Pretty Function Name">
          <TextInput
            value={merged.pretty_function_name}
            onChange={set("pretty_function_name")}
          />
        </Field>
      )}
      {!hideCallUrl && (
        <Field label="Call Function Name">
          <TextInput
            value={merged.call_function_name}
            onChange={set("call_function_name")}
          />
        </Field>
      )}
      {!hideContentTypeUrl && (
        <Field label="Content Type">
          <TextInput
            value={merged.content_type}
            onChange={set("content_type")}
          />
        </Field>
      )}

      <Field label="Change Frequency">
        <select
          value={merged.changefreq}
          onChange={set("changefreq")}
          className="w-full text-xs rounded-lg px-3 py-2 outline-none focus:ring-2"
          style={selectStyle}
        >
          {CHANGEFREQ_OPTIONS.map((f) => (
            <option key={f} value={f}>
              {f}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Priority">
        <select
          value={merged.priority_screen}
          onChange={set("priority_screen")}
          className="w-full text-xs rounded-lg px-3 py-2 outline-none focus:ring-2"
          style={selectStyle}
        >
          {PRIORITY_OPTIONS.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
      </Field>
    </CollapsibleSection>
  );
}
